/**
 * The Glass — communications (issue #964): what LegalFlare sends to a Firm's
 * Contact or a Potential Client, one card per email or page, each beside the
 * example rendered from the code that composes it.
 *
 * Renders the activeness.yaml entry of kind `communications-roster`, packaged
 * verbatim at build time (GLASS_CANON.stewardship), and the committed examples
 * under engineering/docs/communications/, which apps/web/scripts/
 * copy-communications.mjs copies into the SPA's public/communications/ before
 * dev and build. This pane reads NO live system at render time.
 *
 * The glass constitution (design-786 §2.10) governs the cards: render canon,
 * never author state — every label on a card is the roster's own text; the
 * one derivation is the grouping, by the entry's `audience` (an audience that
 * starts "Potential Client" is shown under Potential Clients, every other
 * under Firms' Contacts). The examples are the declared exception to "never a
 * committed copy" (the Glass front page's rule): they are generated files
 * committed under engineering/docs/communications/ and served as static
 * assets, not part of GLASS_CANON — a picture cannot be packaged as canon
 * text. They carry fixture data only, and a Client-Facing Email example shows
 * LegalFlare's standard wording, not any one Firm's approved template.
 */

import {
  GLASS_CANON,
  type GlassActivenessSurface,
  type GlassCommunication,
} from "@command-center/shared";

const KIND = "communications-roster";
const DOCS_PREFIX = "engineering/docs/communications/";

/** The committed example path → the path the SPA serves it at. */
function servedPath(example: string): string {
  return "/communications/" + example.slice(DOCS_PREFIX.length);
}

function pngOf(example: string): string {
  return servedPath(example).replace(/\.html$/, ".png");
}

export function GlassCommunicationsPage() {
  const rosters: GlassActivenessSurface[] =
    GLASS_CANON.stewardship.activeness.data.surfaces.filter(
      (s) => s.kind === KIND,
    );
  const all: GlassCommunication[] = rosters.flatMap(
    (r) => r.communications ?? [],
  );
  const toClients = all.filter((c) => c.audience.startsWith("Potential Client"));
  const toFirms = all.filter((c) => !c.audience.startsWith("Potential Client"));

  return (
    <section className="mx-auto max-w-6xl p-6">
      <h1 className="mb-1 text-xl tracking-tight">Communications</h1>
      <p className="mb-1 text-sm text-plane-muted">
        What LegalFlare sends to a Firm's Contact or a Potential Client — one
        example of each, rendered from the code that composes it with fixture
        data, beside what it says, what sends it, who gets it, and what the
        recipient can do.
      </p>
      <p className="mb-5 text-xs text-plane-muted">
        Renders the <code>{KIND}</code> entry of{" "}
        <code>{GLASS_CANON.meta.sources.activeness}</code> and the examples in{" "}
        <code>{DOCS_PREFIX}</code>, packaged at build time.
      </p>

      {rosters.map((r) => (
        <p key={r.id} className="mb-5 whitespace-pre-wrap text-xs text-plane-muted" data-testid="communications-note">
          {r.note}
        </p>
      ))}

      <Group title="To Firms' Contacts" items={toFirms} testId="communications-firms" />
      <Group title="To Potential Clients" items={toClients} testId="communications-clients" />
    </section>
  );
}

function Group({
  title,
  items,
  testId,
}: {
  title: string;
  items: GlassCommunication[];
  testId: string;
}) {
  return (
    <div className="mb-10" data-testid={testId}>
      <h2 className="mb-3 text-base font-semibold">
        {title} <span className="text-xs font-normal text-plane-muted">({items.length})</span>
      </h2>
      <div className="space-y-6">
        {items.map((c) => (
          <Card key={c.id} c={c} />
        ))}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-plane-muted">
        {label}
      </div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function Card({ c }: { c: GlassCommunication }) {
  return (
    <article
      id={c.id}
      className="scroll-mt-6 rounded-md border border-border bg-card p-4"
      data-testid={`communication-${c.id}`}
    >
      <div className="mb-3 flex flex-wrap items-baseline gap-x-3">
        <h3 className="text-base font-semibold">{c.name}</h3>
        <code className="text-xs text-plane-muted">{c.id}</code>
        <span className="text-xs text-plane-muted">
          {c.glossary_kind} · {c.channel} · {c.stamp.toUpperCase()}
        </span>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <Field label="Subject">{c.subject}</Field>
          <Field label="Audience">{c.audience}</Field>
          <Field label="Sent by">{c.sender}</Field>
          <Field label="When">{c.trigger}</Field>
          <Field label="Opt-in">{c.opt_in}</Field>
          <Field label="Says">{c.says}</Field>
          <Field label="Recipient can">
            <ul className="list-disc pl-5">
              {c.recipient_can.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </Field>
          {c.note !== undefined ? <Field label="Note">{c.note}</Field> : null}
          <Field label="Process">
            <code>{c.process}</code> (bus matrix)
          </Field>
        </div>
        <div className="space-y-3">
          {c.examples.map((ex) => (
            <figure key={ex}>
              <iframe
                title={`${c.name} — example`}
                src={servedPath(ex)}
                sandbox=""
                className="h-[480px] w-full rounded border border-border bg-white"
              />
              <figcaption className="mt-1 text-xs text-plane-muted">
                <code>{ex}</code> ·{" "}
                <a className="underline" href={servedPath(ex)} target="_blank" rel="noreferrer">
                  open
                </a>{" "}
                ·{" "}
                <a className="underline" href={pngOf(ex)} target="_blank" rel="noreferrer">
                  PNG
                </a>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </article>
  );
}
